/**
 * @file rest/totp.ts
 * @tickets TOTP-01, TOTP-02, TOTP-03
 *
 * Client SDK pour les endpoints TOTP :
 *
 * Endpoints admin (ADMIN/ROOT) :
 *   - setup()         : générer un secret + URI otpauth://
 *   - enable()        : activer le TOTP après scan du QR code
 *   - disable()       : désactiver le TOTP (confirmation mot de passe)
 *   - regenRecovery() : regénérer les codes de récupération (TOTP-02)
 *
 * Endpoints auth :
 *   - verify()        : échanger temp_token + code TOTP → TokenPair
 *   - recover()       : échanger temp_token + code de récupération → TokenPair
 *
 * Endpoints utilisateur (tout utilisateur, TOTP-03) :
 *   - userSetup()     : générer un secret TOTP (access JWT ou setup_token)
 *   - userEnable()    : activer le TOTP (access JWT ou setup_token)
 *   - userDisable()   : désactiver le TOTP (access JWT + confirmation mot de passe)
 *
 * ⚠ Les endpoints admin nécessitent un JWT valide (ADMIN ou ROOT).
 * ⚠ Si le backend a TOTP_ENABLED=false, setup/enable/disable retournent 503.
 *
 * @example
 * ```ts
 * // Flow admin : setup + activation
 * const { data: setup } = await sdk.totp.setup();
 * // Afficher setup.otpauth_uri en QR code (react-qr-code)
 * const { data: activation } = await sdk.totp.enable('123456');
 * console.log('Codes de récupération :', activation.recovery_codes);
 *
 * // Flow login 2FA classique
 * const loginResp = await sdk.auth.login(email, password);
 * if ('temp_token' in loginResp.data) {
 *   const { data: tokens } = await sdk.totp.verify(loginResp.data.temp_token, '654321');
 *   sdk.tokenManager.store(tokens.access_token, tokens.refresh_token);
 * }
 *
 * // Flow C4 obligatoire (TOTP-03)
 * if ('setup_token' in loginResp.data) {
 *   const { data: s } = await sdk.totp.userSetup(loginResp.data.setup_token);
 *   // Afficher s.otpauth_uri → scanner
 *   const { data: e } = await sdk.totp.userEnable('123456', loginResp.data.setup_token);
 *   // Retenter le login → TotpChallengeResponse → verify → TokenPair
 * }
 * ```
 */
// ── Implémentation ────────────────────────────────────────────────────────────
/** Simulation data par défaut pour les endpoints TOTP */
const _FAKE_SECRET = 'JBSWY3DPEHPK3PXP';
const _FAKE_RECOVERY_CODES = Array.from({ length: 8 }, (_, i) => `DEMO-FAKE-CODE-000${i + 1}`);
const _FAKE_TOKEN_PAIR = {
    access_token: 'sim_access_token',
    refresh_token: 'sim_refresh_token',
    token_type: 'bearer',
    expires_in: 3600,
};
export class TotpClientImpl {
    http;
    constructor(http) {
        this.http = http;
    }
    // ── Admin ─────────────────────────────────────────────────────────────────
    async setup() {
        return this.http.post('/api/v1/admin/totp/setup', {}, async () => ({
            otpauth_uri: `otpauth://totp/Assistia:demo@luminya.eu?secret=${_FAKE_SECRET}&issuer=Assistia`,
            secret: _FAKE_SECRET,
        }));
    }
    async enable(code) {
        return this.http.post('/api/v1/admin/totp/enable', { code }, async () => ({ recovery_codes: _FAKE_RECOVERY_CODES }));
    }
    async disable(password) {
        return this.http.post('/api/v1/admin/totp/disable', { password }, async () => undefined);
    }
    async regenRecovery(code) {
        return this.http.post('/api/v1/admin/totp/regen-recovery', { code }, async () => ({ recovery_codes: _FAKE_RECOVERY_CODES }));
    }
    // ── Auth ──────────────────────────────────────────────────────────────────
    async verify(tempToken, code) {
        return this.http.post('/api/v1/auth/totp/verify', { temp_token: tempToken, code }, async () => _FAKE_TOKEN_PAIR);
    }
    async recover(tempToken, recoveryCode) {
        return this.http.post('/api/v1/auth/totp/recover', { temp_token: tempToken, recovery_code: recoveryCode }, async () => _FAKE_TOKEN_PAIR);
    }
    // ── Utilisateur (TOTP-03) ─────────────────────────────────────────────────
    async userSetup(bearerOverride) {
        return this.http.post('/api/v1/user/totp/setup', {}, async () => ({
            otpauth_uri: `otpauth://totp/Assistia:demo@luminya.eu?secret=${_FAKE_SECRET}&issuer=Assistia`,
            secret: _FAKE_SECRET,
        }), bearerOverride ? { bearerOverride } : undefined);
    }
    async userEnable(code, bearerOverride) {
        return this.http.post('/api/v1/user/totp/enable', { code }, async () => ({ recovery_codes: _FAKE_RECOVERY_CODES }), bearerOverride ? { bearerOverride } : undefined);
    }
    async userDisable(password) {
        return this.http.post('/api/v1/user/totp/disable', { password }, async () => undefined);
    }
}
//# sourceMappingURL=totp.js.map